import React, { useEffect, useState } from 'react'
import styled from 'styled-components'
import { motion, AnimatePresence } from 'framer-motion'
import { FadeInUp, StaggerContainer, StaggerItem } from '../Components/Animations'

// Design tokens
const colors = {
  primary: '#0d9488',
  primaryLight: '#14b8a6',
  primaryDark: '#0f766e',
  secondary: '#0891b2',
  accent: '#5eead4',
  dark: '#0f172a',
  darkBlue: '#1e3a5f',
  text: '#334155',
  textLight: '#64748b',
  white: '#ffffff',
  background: '#f8fafc',
  backgroundAlt: '#f0fdfa'
}

const Container = styled.div`
  width: 100%;
  min-height: 100vh;
  background-color: ${colors.white};
  font-family: -apple-system, system-ui, BlinkMacSystemFont, 'Segoe UI', Helvetica, Arial, sans-serif;
`

const MaxWidthContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 24px;
  @media (max-width: 768px) {
    padding: 0 16px;
  }
`

// Hero Section
const HeroSection = styled.section`
  padding: 160px 0 80px;
  background: linear-gradient(135deg, ${colors.dark} 0%, ${colors.darkBlue} 100%);
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: radial-gradient(circle at 20% 80%, rgba(20, 184, 166, 0.15) 0%, transparent 50%),
                radial-gradient(circle at 80% 20%, rgba(8, 145, 178, 0.15) 0%, transparent 50%);
  }

  @media (max-width: 768px) {
    padding: 120px 0 60px;
  }
`

const HeroContent = styled.div`
  position: relative;
  z-index: 1;
  text-align: center;
  max-width: 900px;
  margin: 0 auto;
`

const HeroLabel = styled.span`
  display: inline-block;
  background: rgba(20, 184, 166, 0.2);
  color: ${colors.accent};
  padding: 8px 20px;
  border-radius: 50px;
  font-size: 14px;
  font-weight: 500;
  letter-spacing: 1px;
  text-transform: uppercase;
  margin-bottom: 24px;
`

const HeroTitle = styled.h1`
  font-size: 56px;
  font-weight: 700;
  color: ${colors.white};
  margin-bottom: 24px;
  line-height: 1.1;

  @media (max-width: 768px) {
    font-size: 36px;
  }
`

const HeroSubtitle = styled.p`
  font-size: 20px;
  color: rgba(255, 255, 255, 0.8);
  line-height: 1.7;
  max-width: 700px;
  margin: 0 auto;

  @media (max-width: 768px) {
    font-size: 16px;
  }
`

// Main Section
const Section = styled.section`
  padding: 100px 0;
  background: ${colors.background};

  @media (max-width: 768px) {
    padding: 60px 0;
  }
`

// Category Accordion
const CategorySection = styled.div`
  margin-bottom: 20px;
  border-radius: 16px;
  overflow: hidden;
  background: ${colors.white};
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.04);
  border: 1px solid rgba(0, 0, 0, 0.05);
  transition: all 0.3s ease;

  &:hover {
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.08);
  }
`

const CategoryHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 28px 32px;
  cursor: pointer;
  transition: all 0.3s ease;
  background: ${props => props.isExpanded
    ? `linear-gradient(135deg, ${colors.dark} 0%, ${colors.darkBlue} 100%)`
    : colors.white};

  @media (max-width: 768px) {
    padding: 20px;
  }
`

const CategoryInfo = styled.div`
  flex: 1;
`

const CategoryTitle = styled.h2`
  font-size: 24px;
  font-weight: 700;
  color: ${props => props.isExpanded ? colors.white : colors.dark};
  margin: 0 0 8px 0;
  transition: all 0.3s ease;

  @media (max-width: 768px) {
    font-size: 20px;
  }
`

const CategoryCount = styled.span`
  font-size: 14px;
  color: ${props => props.isExpanded ? colors.accent : colors.primary};
  font-weight: 500;
`

const ExpandButton = styled(motion.div)`
  width: 44px;
  height: 44px;
  border-radius: 12px;
  background: ${props => props.isExpanded
    ? 'rgba(255, 255, 255, 0.15)'
    : `linear-gradient(135deg, ${colors.primary}, ${colors.secondary})`};
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  font-weight: 500;
  flex-shrink: 0;
  margin-left: 20px;
`

const CategoryContent = styled(motion.div)`
  overflow: hidden;
`

const CategoryDescription = styled.p`
  font-size: 16px;
  color: ${colors.textLight};
  padding: 24px 32px;
  line-height: 1.7;
  margin: 0;
  background: ${colors.backgroundAlt};
  border-top: 1px solid rgba(0, 0, 0, 0.05);

  @media (max-width: 768px) {
    padding: 20px;
  }
`

const CaseStudiesList = styled.div`
  padding: 24px 32px 32px;

  @media (max-width: 768px) {
    padding: 20px;
  }
`

const CaseStudyItem = styled(motion.div)`
  padding: 28px;
  margin-bottom: 16px;
  background: ${colors.background};
  border-radius: 12px;
  position: relative;
  overflow: hidden;
  transition: all 0.3s ease;

  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    width: 4px;
    background: linear-gradient(180deg, ${colors.primary}, ${colors.secondary});
  }

  &:hover {
    background: ${colors.backgroundAlt};
    transform: translateX(8px);
  }

  &:last-child {
    margin-bottom: 0;
  }

  @media (max-width: 768px) {
    padding: 20px;
  }
`

const CaseStudyTitle = styled.h3`
  font-size: 20px;
  font-weight: 700;
  color: ${colors.dark};
  margin: 0 0 8px 0;
`

const CaseStudyClient = styled.p`
  font-size: 14px;
  color: ${colors.primary};
  font-weight: 600;
  margin: 0 0 16px 0;
`

const CaseStudyDescription = styled.p`
  font-size: 16px;
  line-height: 1.7;
  color: ${colors.textLight};
  margin: 0 0 20px 0;
`

const CaseStudyImpact = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: linear-gradient(135deg, ${colors.primary}, ${colors.secondary});
  color: white;
  padding: 10px 20px;
  border-radius: 50px;
  font-size: 14px;
  font-weight: 600;

  &::before {
    content: '✓';
  }
`

// Modal Overlay
const ModalOverlay = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(15, 23, 42, 0.8);
  backdrop-filter: blur(8px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
`

const ModalContent = styled(motion.div)`
  background: ${colors.white};
  border-radius: 24px;
  max-width: 800px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  position: relative;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);

  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-track {
    background: ${colors.background};
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb {
    background: ${colors.primary};
    border-radius: 4px;
  }
`

const ModalHeader = styled.div`
  background: linear-gradient(135deg, ${colors.dark} 0%, ${colors.darkBlue} 100%);
  padding: 40px;
  position: relative;

  @media (max-width: 768px) {
    padding: 30px 20px;
  }
`

const CloseButton = styled.button`
  position: absolute;
  top: 20px;
  right: 20px;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.15);
  border: none;
  color: white;
  font-size: 24px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.25);
    transform: rotate(90deg);
  }
`

const ModalTitle = styled.h2`
  font-size: 32px;
  font-weight: 700;
  color: ${colors.white};
  margin: 0 0 8px 0;

  @media (max-width: 768px) {
    font-size: 24px;
  }
`

const ModalClient = styled.p`
  font-size: 16px;
  color: ${colors.accent};
  font-weight: 500;
  margin: 0;
`

const ModalBody = styled.div`
  padding: 40px;

  @media (max-width: 768px) {
    padding: 24px 20px;
  }
`

const DetailSection = styled.div`
  margin-bottom: 32px;

  &:last-child {
    margin-bottom: 0;
  }
`

const DetailTitle = styled.h3`
  font-size: 18px;
  font-weight: 700;
  color: ${colors.dark};
  margin: 0 0 12px 0;
  display: flex;
  align-items: center;
  gap: 10px;

  &::before {
    content: '';
    width: 4px;
    height: 20px;
    background: linear-gradient(180deg, ${colors.primary}, ${colors.secondary});
    border-radius: 2px;
  }
`

const DetailText = styled.p`
  font-size: 16px;
  line-height: 1.8;
  color: ${colors.text};
  margin: 0;
`

const TechStackContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-top: 8px;
`

const TechBadge = styled.span`
  background: ${colors.backgroundAlt};
  color: ${colors.primary};
  padding: 8px 16px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  border: 1px solid rgba(13, 148, 136, 0.2);
  transition: all 0.3s ease;

  &:hover {
    background: ${colors.primary};
    color: ${colors.white};
  }
`

const ChallengesList = styled.ul`
  margin: 0;
  padding-left: 20px;
  color: ${colors.text};

  li {
    margin-bottom: 8px;
    line-height: 1.7;

    &:last-child {
      margin-bottom: 0;
    }
  }
`

const ViewDetailsButton = styled.button`
  background: transparent;
  border: 2px solid ${colors.primary};
  color: ${colors.primary};
  padding: 8px 20px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  margin-left: 16px;
  transition: all 0.3s ease;

  &:hover {
    background: ${colors.primary};
    color: ${colors.white};
  }

  @media (max-width: 768px) {
    margin-left: 0;
    margin-top: 12px;
    width: 100%;
  }
`

const CaseStudyFooter = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
  }
`

const RSNGCaseStudies = () => {
  const [expandedCategories, setExpandedCategories] = useState({})
  const [selectedCaseStudy, setSelectedCaseStudy] = useState(null)

  useEffect(() => {
    window.scrollTo(0, 0)
  }, [])

  // Close modal on Escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        setSelectedCaseStudy(null)
      }
    }
    window.addEventListener('keydown', handleEscape)
    return () => window.removeEventListener('keydown', handleEscape)
  }, [])

  const toggleCategory = (index) => {
    setExpandedCategories(prev => ({
      ...prev,
      [index]: !prev[index]
    }))
  }

  const caseStudyCategories = [
    {
      category: 'DevOps & Cloud Solutions',
      description: 'Transforming deployment processes and cloud infrastructure for optimal performance and cost efficiency.',
      cases: [
        {
          title: 'E-commerce Giant',
          client: 'Leading E-commerce Platform',
          description: 'Reduced deployment time by 85%, from 8 hours to under 45 minutes, through sophisticated CI/CD automation that revolutionized their release process.',
          impact: '85% faster deployments',
          hasDetails: true,
          details: {
            intro: 'A leading e-commerce platform processing over 10 million transactions daily was struggling with lengthy deployment cycles that hindered their ability to respond to market demands and customer needs. Their legacy deployment process was manual, error-prone, and required extensive coordination between multiple teams.',
            businessObjective: 'The primary goal was to dramatically reduce deployment time while maintaining system reliability and security. The client aimed to achieve continuous delivery capabilities, enabling multiple daily deployments instead of weekly releases, ultimately improving time-to-market for new features and bug fixes.',
            solution: 'We implemented a comprehensive CI/CD pipeline using industry-leading tools and practices. This included automated testing at every stage, containerization with Docker, orchestration with Kubernetes, and infrastructure as code using Terraform. We also introduced blue-green deployments to ensure zero-downtime releases and implemented automated rollback mechanisms for quick recovery from any issues.',
            challenges: [
              'Legacy monolithic architecture required careful decomposition into microservices',
              'Existing manual processes had undocumented dependencies',
              'Team resistance to change and need for extensive training',
              'Maintaining 99.9% uptime during the transition period',
              'Integrating with existing security and compliance requirements'
            ],
            techStack: ['Jenkins', 'Docker', 'Kubernetes', 'Terraform', 'AWS', 'GitLab CI', 'Prometheus', 'Grafana', 'Helm', 'ArgoCD']
          }
        },
        {
          title: 'FinTech Company',
          client: 'FinTech Startup',
          description: 'Achieved 40% cloud cost savings and dramatically improved performance by migrating to Kubernetes with intelligent auto-scaling infrastructure.',
          impact: '40% cost reduction',
          hasDetails: true,
          details: {
            intro: 'A rapidly growing FinTech startup was experiencing escalating cloud costs and performance bottlenecks as their user base expanded. Their existing infrastructure on traditional VMs was unable to handle traffic spikes efficiently, leading to poor user experience during peak trading hours.',
            businessObjective: 'The company needed to reduce operational costs while improving application performance and scalability. They aimed to handle 10x traffic spikes without manual intervention and reduce their monthly cloud spend by at least 30%.',
            solution: 'We designed and implemented a complete Kubernetes migration strategy with intelligent auto-scaling based on custom metrics. This included horizontal pod autoscaling, cluster autoscaling, and spot instance integration for non-critical workloads. We also implemented resource quotas and limit ranges to prevent runaway costs.',
            challenges: [
              'Zero-downtime migration requirement during active trading hours',
              'Complex stateful applications requiring careful data migration',
              'Integration with existing compliance and audit logging systems',
              'Training development teams on Kubernetes best practices',
              'Implementing proper security policies and network segmentation'
            ],
            techStack: ['Kubernetes', 'Helm', 'Istio', 'Prometheus', 'AWS EKS', 'Terraform', 'Datadog', 'PagerDuty', 'Vault', 'ArgoCD']
          }
        },
        {
          title: 'SaaS Provider',
          client: 'SaaS Company',
          description: 'Delivered 99.99% uptime over 12 consecutive months by implementing a comprehensive Site Reliability Engineering (SRE) model with real-time monitoring.',
          impact: '99.99% uptime',
          hasDetails: true,
          details: {
            intro: 'A B2B SaaS company serving enterprise clients was struggling with recurring outages and slow incident response times. Their clients were demanding strict SLAs, and the lack of proper monitoring and incident management was putting major contracts at risk.',
            businessObjective: 'Achieve 99.99% uptime (less than 52 minutes of downtime per year), reduce mean time to detection (MTTD) to under 2 minutes, and mean time to resolution (MTTR) to under 30 minutes for critical incidents.',
            solution: 'We implemented a comprehensive SRE model including 24/7 monitoring dashboards, automated alerting with intelligent escalation, runbooks for common incidents, chaos engineering practices, and regular game days. We also established error budgets and SLO-based release gates.',
            challenges: [
              'Cultural shift from reactive to proactive incident management',
              'Establishing meaningful SLIs and SLOs across all services',
              'Building a robust on-call rotation without burning out the team',
              'Implementing chaos engineering without impacting production',
              'Creating comprehensive runbooks for hundreds of failure scenarios'
            ],
            techStack: ['Prometheus', 'Grafana', 'PagerDuty', 'Elasticsearch', 'Kibana', 'Jaeger', 'OpenTelemetry', 'Chaos Monkey', 'Statuspage', 'Slack']
          }
        }
      ]
    },
    {
      category: 'Data Engineering',
      description: 'Building robust data pipelines and ETL solutions for healthcare organizations to process millions of records efficiently.',
      cases: [
        {
          title: 'Agent Centric',
          client: 'Medicare Sales Platform',
          description: 'Agent Centric is a platform that helps Medicare Sales Agents manage their Sales and Marketing activity, processing millions of claims data annually.',
          impact: 'Improved data accuracy & efficiency',
          hasDetails: true,
          details: {
            intro: 'Agent Centric serves thousands of Medicare Sales Agents across the country, requiring a robust data platform to handle millions of claims records, commission calculations, and compliance reporting. The legacy system was causing delays in commission payments and compliance issues.',
            businessObjective: 'Build a scalable data platform capable of processing 5+ million claims daily with real-time commission calculations, automated compliance reporting, and self-service analytics for agents and administrators.',
            solution: 'We designed a modern data lakehouse architecture using cloud-native technologies. This included real-time data ingestion pipelines, automated data quality checks, dimensional modeling for analytics, and a semantic layer for self-service BI. We also implemented automated reconciliation processes.',
            challenges: [
              'Migrating 10+ years of historical claims data without disruption',
              'Ensuring HIPAA compliance throughout the data pipeline',
              'Complex commission calculation rules with frequent policy changes',
              'Real-time synchronization between multiple source systems',
              'Building trust in data accuracy with skeptical stakeholders'
            ],
            techStack: ['Apache Spark', 'Databricks', 'Delta Lake', 'Airflow', 'dbt', 'Snowflake', 'AWS S3', 'Kafka', 'Great Expectations', 'Tableau']
          }
        },
        {
          title: 'MSC Application',
          client: 'Member Services Center',
          description: 'The MSC Application is designed to improve member experience by offering comprehensive support and efficient management of healthcare benefits, claims, and services.',
          impact: 'Real-time insights & automation',
          hasDetails: true,
          details: {
            intro: 'The Member Services Center handles thousands of member inquiries daily, but agents lacked a unified view of member data, leading to long call times and frustrated members. Data was scattered across multiple legacy systems with no real-time visibility.',
            businessObjective: 'Create a 360-degree member view with real-time data integration, reducing average call handling time by 40% and improving first-call resolution rates to above 85%.',
            solution: 'We built a real-time data integration platform that consolidates member data from 12+ source systems into a unified member profile. This included CDC (Change Data Capture) pipelines, a real-time serving layer, and integration with the call center application for instant data access.',
            challenges: [
              'Integrating with 12+ legacy systems with different data formats',
              'Sub-second latency requirements for call center integration',
              'Complex member matching logic across systems without SSN',
              'Handling data corrections and retroactive changes',
              'Training call center staff on the new unified interface'
            ],
            techStack: ['Kafka', 'Kafka Connect', 'Redis', 'PostgreSQL', 'Debezium', 'Apache Flink', 'Elasticsearch', 'GraphQL', 'React', 'AWS']
          }
        },
        {
          title: 'ACA Automation',
          client: 'Affordable Care Act Platform',
          description: 'ACA plans through the Health Insurance Marketplace, covering hospitalizations, prescription drugs, and mental health services.',
          impact: 'Unified data platform',
          hasDetails: true,
          details: {
            intro: 'Managing ACA plan data across the Health Insurance Marketplace required processing complex eligibility rules, subsidy calculations, and enrollment data from multiple state and federal exchanges. Manual processes were causing enrollment errors and compliance risks.',
            businessObjective: 'Automate ACA enrollment processing, reduce manual intervention by 80%, and ensure 100% compliance with CMS reporting requirements while handling the annual Open Enrollment surge.',
            solution: 'We developed an automated data platform for ACA enrollment processing with real-time eligibility verification, automated subsidy calculations, and comprehensive audit trails. The system includes automated CMS report generation and anomaly detection for potential issues.',
            challenges: [
              'Complex and frequently changing ACA regulations',
              'Handling 10x traffic spikes during Open Enrollment periods',
              'Integration with state-based exchanges with varying standards',
              'Strict CMS reporting deadlines with zero tolerance for errors',
              'Maintaining data lineage for audit and compliance purposes'
            ],
            techStack: ['Python', 'Apache Airflow', 'Snowflake', 'dbt', 'AWS Lambda', 'Step Functions', 'S3', 'Redshift', 'QuickSight', 'Terraform']
          }
        },
        {
          title: 'MTS Fraud Detection',
          client: 'Member Tracking System',
          description: 'The MTS (Member Tracking System) is designed to track and manage member interactions, healthcare utilization, claims data, and outcomes.',
          impact: 'Enhanced fraud detection',
          hasDetails: true,
          details: {
            intro: 'The Member Tracking System processes millions of healthcare claims monthly, but fraudulent claims were slipping through manual review processes, costing millions in improper payments. The existing rules-based system had high false positive rates.',
            businessObjective: 'Implement an advanced fraud detection system that identifies suspicious claims in real-time, reduces false positives by 50%, and recovers at least $10M annually in fraudulent payments.',
            solution: 'We built a multi-layered fraud detection platform combining rules-based screening with machine learning models. This includes real-time claim scoring, network analysis for provider collusion detection, and automated case management for investigators.',
            challenges: [
              'Highly imbalanced dataset with fraud representing <1% of claims',
              'Evolving fraud patterns requiring continuous model retraining',
              'Explainability requirements for regulatory compliance',
              'Integration with existing claims adjudication workflow',
              'Balancing fraud detection with member experience'
            ],
            techStack: ['Python', 'scikit-learn', 'XGBoost', 'Apache Spark', 'MLflow', 'Kafka', 'Neo4j', 'Airflow', 'Snowflake', 'Looker']
          }
        },
        {
          title: 'ADM - GCP Migration',
          client: 'Cloud Migration',
          description: 'Used data to improve medication adherence rates by analyzing prescription and patient behavior data with predictive models.',
          impact: 'Successful cloud migration',
          hasDetails: true,
          details: {
            intro: 'The Adherence Data Management system was running on aging on-premises infrastructure with limited scalability. The organization needed to migrate to the cloud while enhancing their medication adherence analytics capabilities.',
            businessObjective: 'Migrate the entire ADM platform to Google Cloud Platform, improve query performance by 5x, enable advanced analytics capabilities, and reduce infrastructure costs by 35%.',
            solution: 'We executed a comprehensive cloud migration strategy including data warehouse modernization to BigQuery, pipeline migration to Cloud Dataflow, and implementation of Vertex AI for predictive adherence modeling. The new platform enables real-time adherence scoring and intervention recommendations.',
            challenges: [
              'Migrating 50TB+ of historical data with minimal downtime',
              'Rewriting legacy stored procedures for BigQuery compatibility',
              'Ensuring PHI security throughout the migration process',
              'Training the team on GCP services and best practices',
              'Maintaining regulatory compliance during transition'
            ],
            techStack: ['Google BigQuery', 'Cloud Dataflow', 'Vertex AI', 'Cloud Composer', 'Pub/Sub', 'Cloud Functions', 'Looker', 'dbt', 'Terraform', 'Python']
          }
        }
      ]
    },
    {
      category: 'AI Services',
      description: 'Leveraging artificial intelligence and machine learning to solve complex healthcare and financial challenges with intelligent automation.',
      cases: [
        {
          title: 'Agent Centric FAQ Chatbot',
          client: 'AI-Driven Support System',
          description: 'Streamline support for agents using the Agent Centric platform by efficiently resolving inquiries through an AI-driven FAQ system.',
          impact: 'Enhanced operational efficiency',
          hasDetails: true,
          details: {
            intro: 'Medicare Sales Agents using the Agent Centric platform were overwhelming the support team with repetitive questions about commissions, compliance, and platform features. Support ticket volume was growing 30% monthly, and response times were suffering.',
            businessObjective: 'Reduce support ticket volume by 60% through an intelligent FAQ chatbot that can handle common inquiries instantly, while seamlessly escalating complex issues to human agents.',
            solution: 'We developed an AI-powered chatbot using natural language processing and a knowledge base curated from historical support tickets. The system uses intent classification, entity extraction, and contextual understanding to provide accurate answers and can hand off to human agents when needed.',
            challenges: [
              'Building a comprehensive knowledge base from unstructured support data',
              'Handling domain-specific Medicare terminology and regulations',
              'Ensuring accurate responses for compliance-sensitive questions',
              'Seamless handoff to human agents for complex issues',
              'Continuous improvement based on user feedback and new content'
            ],
            techStack: ['Python', 'LangChain', 'OpenAI GPT-4', 'Pinecone', 'FastAPI', 'Redis', 'PostgreSQL', 'React', 'AWS Lambda', 'CloudWatch']
          }
        },
        {
          title: 'Personalized Plan Recommendation',
          client: 'Health Plan Selection Platform',
          description: 'Simplify health and benefit plan selection through personalized recommendations by analyzing PDFs containing plan details, benefits, costs, and eligibility.',
          impact: 'Streamlined decision-making',
          hasDetails: true,
          details: {
            intro: 'Consumers and employers struggle to choose the right health plans from hundreds of options with complex benefits, costs, and eligibility rules buried in lengthy PDF documents. This leads to poor plan selection and member dissatisfaction.',
            businessObjective: 'Create an AI-powered recommendation engine that analyzes plan documents and user preferences to suggest the top 3 most suitable plans, improving plan selection accuracy by 40%.',
            solution: 'We built an intelligent document processing pipeline that extracts and structures plan information from PDFs using OCR and NLP. A recommendation engine then matches user health profiles, preferences, and budget constraints to suggest optimal plans with clear explanations.',
            challenges: [
              'Extracting structured data from varied PDF formats and layouts',
              'Standardizing benefits terminology across different insurers',
              'Building explainable recommendations for regulatory compliance',
              'Handling edge cases in eligibility and coverage rules',
              'Keeping plan data updated during annual enrollment changes'
            ],
            techStack: ['Python', 'PyPDF2', 'Tesseract OCR', 'spaCy', 'Anthropic Claude', 'ChromaDB', 'FastAPI', 'React', 'PostgreSQL', 'AWS']
          }
        },
        {
          title: 'Caregiving Companion',
          client: 'AI-Driven Healthcare Assistant',
          description: 'Develop an AI-driven assistant to provide caregivers with accurate, personalized, and timely disease-specific healthcare information.',
          impact: 'Reliable healthcare information',
          hasDetails: true,
          details: {
            intro: 'Family caregivers managing loved ones with chronic conditions often feel overwhelmed and lack access to reliable, personalized healthcare information. They resort to unreliable internet searches, leading to confusion and potentially harmful decisions.',
            businessObjective: 'Develop a trusted AI companion that provides accurate, personalized healthcare guidance for caregivers, reducing caregiver stress and improving care outcomes for patients with chronic conditions.',
            solution: 'We created an AI assistant trained on verified medical literature and clinical guidelines. The system provides personalized guidance based on the patient\'s specific conditions, medications, and care plan. It includes medication reminders, symptom tracking, and alerts for when to seek professional help.',
            challenges: [
              'Ensuring medical accuracy and avoiding harmful advice',
              'Personalizing responses for complex multi-morbidity cases',
              'Building trust with users for sensitive health topics',
              'Compliance with healthcare regulations and data privacy',
              'Integration with electronic health records where available'
            ],
            techStack: ['Python', 'LangChain', 'OpenAI GPT-4', 'FAISS', 'FastAPI', 'React Native', 'PostgreSQL', 'Redis', 'AWS HealthLake', 'HIPAA-compliant infrastructure']
          }
        },
        {
          title: 'SDOH Code Extraction',
          client: 'Clinical Document Processing',
          description: 'Extracting Social Determinants of Health (SDOH) codes from clinical documents with detailed patient medical history.',
          impact: 'Accurate SDOH identification',
          hasDetails: true,
          details: {
            intro: 'Social Determinants of Health significantly impact patient outcomes, but SDOH information is often buried in unstructured clinical notes. Manual extraction is time-consuming and inconsistent, leading to missed opportunities for intervention.',
            businessObjective: 'Automate SDOH code extraction from clinical documents with 95%+ accuracy, enabling population health programs to identify and address social needs that impact health outcomes.',
            solution: 'We developed a clinical NLP pipeline using transformer models fine-tuned on healthcare data. The system identifies SDOH mentions in clinical notes, maps them to ICD-10 Z-codes, and provides confidence scores. Results feed into care management workflows for intervention.',
            challenges: [
              'Handling varied clinical documentation styles and abbreviations',
              'Distinguishing between patient and family member SDOH factors',
              'Mapping free-text mentions to specific ICD-10 Z-codes',
              'Processing high document volumes with low latency',
              'Continuous model improvement with clinician feedback'
            ],
            techStack: ['Python', 'Hugging Face Transformers', 'ClinicalBERT', 'spaCy', 'Apache Spark', 'MLflow', 'FastAPI', 'PostgreSQL', 'Elasticsearch', 'AWS SageMaker']
          }
        },
        {
          title: 'Credit Card Fraud Detection',
          client: 'Financial Security System',
          description: 'Develop a robust machine learning model capable of accurately identifying fraudulent credit card transactions.',
          impact: 'Reduced financial fraud',
          hasDetails: true,
          details: {
            intro: 'A major financial institution was experiencing rising credit card fraud losses while their rules-based detection system generated excessive false positives, frustrating legitimate customers. They needed a smarter approach to fraud detection.',
            businessObjective: 'Reduce fraud losses by 50% while decreasing false positive rates by 40%, improving both security and customer experience through intelligent real-time transaction scoring.',
            solution: 'We developed an ensemble machine learning system combining gradient boosting, neural networks, and anomaly detection. The system scores transactions in real-time considering hundreds of features including behavioral patterns, merchant risk, and device fingerprinting.',
            challenges: [
              'Extreme class imbalance with fraud representing 0.1% of transactions',
              'Sub-100ms latency requirements for real-time authorization',
              'Adversarial fraud patterns that evolve to evade detection',
              'Explaining model decisions for regulatory compliance',
              'Balancing security with customer friction'
            ],
            techStack: ['Python', 'XGBoost', 'TensorFlow', 'Apache Kafka', 'Redis', 'Kubernetes', 'MLflow', 'Feature Store', 'Grafana', 'AWS SageMaker']
          }
        },
        {
          title: 'Insurance Fraud Detection',
          client: 'AI-Driven Claims Analysis',
          description: 'Develop an AI-driven system capable of accurately identifying fraudulent insurance claims to minimize financial losses.',
          impact: 'Enhanced fraud detection',
          hasDetails: true,
          details: {
            intro: 'Insurance fraud costs the industry billions annually, with sophisticated fraud rings exploiting gaps in traditional detection methods. The client\'s Special Investigations Unit was overwhelmed with cases and needed intelligent prioritization.',
            businessObjective: 'Implement an AI-driven fraud detection system that identifies suspicious claims with 90%+ precision, prioritizes SIU investigations, and recovers at least $20M annually in fraudulent claims.',
            solution: 'We built a comprehensive fraud detection platform using machine learning, network analysis, and NLP. The system analyzes claim patterns, provider networks, claimant history, and unstructured documents to generate fraud risk scores and investigation recommendations.',
            challenges: [
              'Detecting organized fraud rings through network analysis',
              'Processing unstructured claim documents and medical records',
              'Integrating with legacy claims management systems',
              'Building explainable models for legal proceedings',
              'Handling multi-state regulatory requirements'
            ],
            techStack: ['Python', 'PyTorch', 'Neo4j', 'Apache Spark', 'Elasticsearch', 'Airflow', 'MLflow', 'FastAPI', 'React', 'AWS']
          }
        }
      ]
    },
    {
      category: 'Cloud Services',
      description: 'Delivering scalable cloud solutions, migrations, and infrastructure optimization for enterprise-level performance and reliability.',
      cases: [
        {
          title: 'Agent Centric',
          client: 'Medicare Sales Platform - Hybrid Cloud',
          description: 'Agent Centric leverages hybrid cloud solutions to manage and process sensitive healthcare data, providing both on-premises security and cloud scalability.',
          impact: 'HIPAA-compliant hybrid cloud',
          hasDetails: true,
          details: {
            intro: 'Agent Centric needed to modernize their infrastructure while maintaining strict HIPAA compliance for sensitive Medicare data. The challenge was balancing the need for cloud scalability with regulatory requirements that certain data remain on-premises.',
            businessObjective: 'Implement a hybrid cloud architecture that provides cloud-native scalability for non-sensitive workloads while keeping PHI on-premises, achieving 99.9% uptime and reducing infrastructure costs by 25%.',
            solution: 'We designed a hybrid cloud architecture with secure connectivity between on-premises data centers and AWS. Sensitive data processing remains on-premises with encrypted replication, while analytics and non-PHI workloads run in the cloud with auto-scaling capabilities.',
            challenges: [
              'Maintaining HIPAA compliance across hybrid infrastructure',
              'Secure, low-latency connectivity between on-premises and cloud',
              'Consistent security policies across both environments',
              'Disaster recovery spanning multiple locations',
              'Training operations team on hybrid cloud management'
            ],
            techStack: ['AWS', 'VMware', 'Direct Connect', 'Kubernetes', 'Terraform', 'Vault', 'Consul', 'Prometheus', 'ELK Stack', 'Palo Alto']
          }
        },
        {
          title: 'Cloud Migration',
          client: 'Azure to GCP Migration',
          description: 'Moved a large-scale data analytics pipeline from Microsoft Azure to Google Cloud Platform to generate insights for real-time business decision-making.',
          impact: 'Successful cloud migration',
          hasDetails: true,
          details: {
            intro: 'The client\'s data analytics platform on Azure was facing performance limitations and escalating costs. They chose to migrate to Google Cloud Platform to leverage BigQuery\'s superior analytics capabilities and reduce their total cost of ownership.',
            businessObjective: 'Complete migration from Azure to GCP within 6 months with zero data loss, improve query performance by 3x, and reduce monthly cloud costs by 30% through better resource optimization.',
            solution: 'We executed a phased migration strategy starting with non-critical workloads. This included migrating data warehouses to BigQuery, transforming ETL pipelines to Cloud Dataflow, and replatforming applications to Cloud Run. We implemented parallel running for validation before cutover.',
            challenges: [
              'Translating Azure-specific services to GCP equivalents',
              'Migrating petabytes of data with minimal downtime',
              'Retraining team from Azure to GCP technologies',
              'Maintaining data consistency during parallel running',
              'Updating all downstream integrations and reports'
            ],
            techStack: ['Google BigQuery', 'Cloud Dataflow', 'Cloud Run', 'Pub/Sub', 'Cloud Storage', 'Cloud Composer', 'Terraform', 'dbt', 'Looker', 'Datadog']
          }
        },
        {
          title: 'MTS SRE Services',
          client: 'Member Tracking System',
          description: 'Empowered teams with a seamless, integrated platform for developing, testing, and deploying distributed applications.',
          impact: 'Improved deployment reliability',
          hasDetails: true,
          details: {
            intro: 'The Member Tracking System development teams were struggling with inconsistent environments, slow deployments, and frequent production incidents. There was no standardized approach to building and deploying the distributed microservices architecture.',
            businessObjective: 'Establish an SRE practice that improves deployment frequency from monthly to daily, reduces change failure rate below 5%, and achieves mean time to recovery under 1 hour for all incidents.',
            solution: 'We implemented a comprehensive developer platform with standardized CI/CD pipelines, Infrastructure as Code, and GitOps practices. This included service mesh for observability, automated canary deployments, and self-service infrastructure provisioning.',
            challenges: [
              'Standardizing practices across 20+ development teams',
              'Building golden paths without restricting team autonomy',
              'Implementing service mesh in brownfield environment',
              'Creating effective SLOs for complex distributed systems',
              'Cultural change from ops tickets to self-service'
            ],
            techStack: ['Kubernetes', 'Istio', 'ArgoCD', 'Backstage', 'Terraform', 'GitHub Actions', 'Prometheus', 'Grafana', 'Jaeger', 'PagerDuty']
          }
        },
        {
          title: 'LeafHome Salesforce Integration',
          client: 'Home Improvement Solutions',
          description: 'LeafHome uses Salesforce to streamline customer support, enhance service efficiency, and improve customer satisfaction across its brands.',
          impact: 'Enhanced customer experience',
          hasDetails: true,
          details: {
            intro: 'LeafHome operates multiple home improvement brands with disconnected customer data across various systems. Customer service representatives lacked a unified view, leading to poor customer experiences and missed cross-sell opportunities.',
            businessObjective: 'Implement a unified Salesforce platform across all brands, achieving 360-degree customer view, reducing average handle time by 30%, and increasing customer satisfaction scores by 20 points.',
            solution: 'We designed and implemented a multi-brand Salesforce Service Cloud solution with custom integrations to ERP, scheduling, and telephony systems. This included unified customer profiles, automated case routing, and real-time dashboards for service metrics.',
            challenges: [
              'Consolidating customer data from 5+ legacy systems',
              'Managing complex business rules across different brands',
              'Integration with legacy on-premises systems',
              'Training 500+ service representatives on new platform',
              'Maintaining service levels during phased rollout'
            ],
            techStack: ['Salesforce Service Cloud', 'MuleSoft', 'Salesforce Flow', 'Apex', 'Lightning Web Components', 'Tableau CRM', 'Amazon Connect', 'AWS Lambda', 'PostgreSQL', 'Heroku']
          }
        },
        {
          title: 'BlueCloud Snowflake',
          client: 'Cloud-Based Data Platform',
          description: 'BlueCloud Snowflake enables businesses to store, process, and analyze large volumes of data efficiently with unified data storage & processing.',
          impact: 'Unified data platform',
          hasDetails: true,
          details: {
            intro: 'The organization had data scattered across multiple databases, data warehouses, and file systems with no unified access layer. Analytics teams spent more time finding and preparing data than analyzing it, severely limiting business intelligence capabilities.',
            businessObjective: 'Create a unified cloud data platform on Snowflake that consolidates all enterprise data, reduces time-to-insight from weeks to hours, and enables self-service analytics for business users.',
            solution: 'We implemented a modern data platform on Snowflake with automated data ingestion from 50+ sources, a governed data catalog, and semantic layer for self-service analytics. This included data quality monitoring, access controls, and cost management dashboards.',
            challenges: [
              'Migrating and consolidating data from 50+ disparate sources',
              'Implementing data governance without slowing down teams',
              'Managing Snowflake costs with unpredictable query patterns',
              'Building a data catalog that users actually adopt',
              'Balancing data democratization with security requirements'
            ],
            techStack: ['Snowflake', 'Fivetran', 'dbt', 'Airflow', 'Alation', 'Sigma Computing', 'Monte Carlo', 'Terraform', 'AWS S3', 'Python']
          }
        }
      ]
    }
  ]

  return (
    <Container>
      {/* Hero Section */}
      <HeroSection>
        <MaxWidthContainer>
          <HeroContent>
            <FadeInUp>
              <HeroLabel>Success Stories</HeroLabel>
            </FadeInUp>
            <FadeInUp delay={0.1}>
              <HeroTitle>Digital Transformation Success Stories</HeroTitle>
            </FadeInUp>
            <FadeInUp delay={0.2}>
              <HeroSubtitle>
                See how we transform ideas into reality with innovative solutions and future-ready technology. Our creative digital solutions deliver measurable results for businesses thriving in today's digital-first world.
              </HeroSubtitle>
            </FadeInUp>
          </HeroContent>
        </MaxWidthContainer>
      </HeroSection>

      {/* Case Studies Section */}
      <Section>
        <MaxWidthContainer>
          <StaggerContainer staggerDelay={0.15}>
            {caseStudyCategories.map((category, catIndex) => {
              const isExpanded = expandedCategories[catIndex]

              return (
                <StaggerItem key={catIndex}>
                  <CategorySection>
                    <CategoryHeader
                      onClick={() => toggleCategory(catIndex)}
                      isExpanded={isExpanded}
                    >
                      <CategoryInfo>
                        <CategoryTitle isExpanded={isExpanded}>
                          {category.category}
                        </CategoryTitle>
                        <CategoryCount isExpanded={isExpanded}>
                          {category.cases.length} case studies
                        </CategoryCount>
                      </CategoryInfo>
                      <ExpandButton
                        isExpanded={isExpanded}
                        animate={{ rotate: isExpanded ? 45 : 0 }}
                        transition={{ duration: 0.2 }}
                      >
                        +
                      </ExpandButton>
                    </CategoryHeader>

                    <AnimatePresence>
                      {isExpanded && (
                        <CategoryContent
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: 'auto', opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.3, ease: 'easeInOut' }}
                        >
                          <CategoryDescription>{category.description}</CategoryDescription>

                          <CaseStudiesList>
                            {category.cases.map((study, index) => (
                              <CaseStudyItem
                                key={index}
                                initial={{ opacity: 0, x: -20 }}
                                animate={{ opacity: 1, x: 0 }}
                                transition={{ delay: index * 0.1 }}
                                style={{ cursor: study.hasDetails ? 'pointer' : 'default' }}
                                onClick={() => study.hasDetails && setSelectedCaseStudy(study)}
                              >
                                <CaseStudyTitle>{study.title}</CaseStudyTitle>
                                <CaseStudyClient>{study.client}</CaseStudyClient>
                                <CaseStudyDescription>{study.description}</CaseStudyDescription>
                                <CaseStudyFooter>
                                  <CaseStudyImpact>{study.impact}</CaseStudyImpact>
                                  {study.hasDetails && (
                                    <ViewDetailsButton
                                      onClick={(e) => {
                                        e.stopPropagation()
                                        setSelectedCaseStudy(study)
                                      }}
                                    >
                                      View Details
                                    </ViewDetailsButton>
                                  )}
                                </CaseStudyFooter>
                              </CaseStudyItem>
                            ))}
                          </CaseStudiesList>
                        </CategoryContent>
                      )}
                    </AnimatePresence>
                  </CategorySection>
                </StaggerItem>
              )
            })}
          </StaggerContainer>
        </MaxWidthContainer>
      </Section>

      {/* Case Study Detail Modal */}
      <AnimatePresence>
        {selectedCaseStudy && selectedCaseStudy.details && (
          <ModalOverlay
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setSelectedCaseStudy(null)}
          >
            <ModalContent
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              transition={{ duration: 0.3 }}
              onClick={(e) => e.stopPropagation()}
            >
              <ModalHeader>
                <CloseButton onClick={() => setSelectedCaseStudy(null)}>
                  &times;
                </CloseButton>
                <ModalTitle>{selectedCaseStudy.title}</ModalTitle>
                <ModalClient>{selectedCaseStudy.client}</ModalClient>
              </ModalHeader>

              <ModalBody>
                <DetailSection>
                  <DetailTitle>Introduction</DetailTitle>
                  <DetailText>{selectedCaseStudy.details.intro}</DetailText>
                </DetailSection>

                <DetailSection>
                  <DetailTitle>Business Objective</DetailTitle>
                  <DetailText>{selectedCaseStudy.details.businessObjective}</DetailText>
                </DetailSection>

                <DetailSection>
                  <DetailTitle>Solution</DetailTitle>
                  <DetailText>{selectedCaseStudy.details.solution}</DetailText>
                </DetailSection>

                <DetailSection>
                  <DetailTitle>Challenges</DetailTitle>
                  <ChallengesList>
                    {selectedCaseStudy.details.challenges.map((challenge, index) => (
                      <li key={index}>{challenge}</li>
                    ))}
                  </ChallengesList>
                </DetailSection>

                <DetailSection>
                  <DetailTitle>Tech Stack</DetailTitle>
                  <TechStackContainer>
                    {selectedCaseStudy.details.techStack.map((tech, index) => (
                      <TechBadge key={index}>{tech}</TechBadge>
                    ))}
                  </TechStackContainer>
                </DetailSection>
              </ModalBody>
            </ModalContent>
          </ModalOverlay>
        )}
      </AnimatePresence>
    </Container>
  )
}

export default RSNGCaseStudies

